import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, HostBinding, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivationEnd } from '@angular/router';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { RouterEventsService } from '@frontend/sports/common/core/utils/router-events';
import { EventSubscription } from '@frontend/sports/event-subscription/feature/base-subscription';
import { DetailedEventSubscriptionService } from '@frontend/sports/event-subscription/feature/detailed-event-subscription';
import { filterSports } from '@frontend/sports/host-app/sports-product/feature/utils';
import { debounce } from 'lodash-es';

import { TabBarItem } from '../tab-bar/tab-bar.models';
import { EventSwitcherToggleComponent } from './event-switcher/event-switcher-toggle/event-switcher-toggle.component';
import { MediaSwitcherComponent } from './media-switcher/media-switcher.component';

@Component({
    selector: 'ms-event-details-breadcrumb',
    templateUrl: 'event-details-breadcrumb.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, MediaSwitcherComponent, EventSwitcherToggleComponent],
})
export class EventDetailsBreadcrumbComponent implements OnInit, OnDestroy {
    @HostBinding('class') className = 'event-details-breadcrumb';

    // This property will be passed to the media switcher and if needed to the event switcher
    @Input() event?: EventModel;
    mediaTabsCount: number;
    tabs?: Promise<TabBarItem[]>;
    private subscription?: EventSubscription<EventModel>;
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private routerEventsService: RouterEventsService,
        private eventSubscriptionService: DetailedEventSubscriptionService,
        private changeRef: ChangeDetectorRef,
        private eventDetailsConfig: EventDetailsConfig,
    ) {}

    ngOnInit(): void {
        if (!this.eventDetailsConfig.showInOverlay) {
            this.routerEventsService.currentActivationEnd
                .pipe(filterSports(), takeUntilDestroyed(this.destroyRef))
                .subscribe((route) => this.activationEnded(route));
        }
    }

    onTabsChanged(data: number) {
        this.mediaTabsCount = data;
        this.changeRef.markForCheck();
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    private eventChanged = (event: EventModel) => {
        this.event = event;
        this.changeRef.markForCheck();
    };

    private activationEnded(route: ActivationEnd | undefined): void {
        this.event = <EventModel>route?.snapshot.data['model'].model;

        if (this.event) {
            this.subscription?.unsubscribe();

            this.subscription = this.eventSubscriptionService.subscribe([
                {
                    event: this.event,
                    callback: debounce(this.eventChanged, 100),
                },
            ]);

            this.changeRef.markForCheck();
        }
    }
}
